import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api, useTheme } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import Sidebar from '../components/layout/Sidebar';
import {
  Users,
  Briefcase,
  Mail,
  CheckCircle,
  Clock,
  TrendingUp,
  Calendar,
  ArrowRight,
  Phone,
  FileText
} from 'lucide-react';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  
  const [stats, setStats] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, tasksRes, contactsRes] = await Promise.all([
        api.get('/reports/summary'),
        api.get('/tasks/today'),
        api.get('/contacts?limit=5')
      ]);
      
      setStats(statsRes.data);
      setTodayTasks(tasksRes.data);
      setRecentContacts(contactsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, {
        completed_at: new Date().toISOString()
      });
      toast.success('Task completed!');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case 'call': return <Phone className="h-4 w-4" />;
      case 'email_follow_up': return <Mail className="h-4 w-4" />;
      case 'request_docs': return <FileText className="h-4 w-4" />;
      default: return <CheckCircle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 ml-64 p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-48"></div>
            <div className="grid grid-cols-4 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-32 bg-muted rounded-xl"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background" data-testid="dashboard-page">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-foreground">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's what's happening with your pipeline today.
          </p>
        </div>

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="hover-lift cursor-pointer" onClick={() => navigate('/contacts')} data-testid="stat-contacts">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Contacts</p>
                  <p className="text-3xl font-bold font-heading mt-1">{stats?.total_contacts || 0}</p>
                </div>
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-muted-foreground">
                <TrendingUp className="h-4 w-4 mr-1 text-green-500" />
                <span className="text-green-500">+12%</span>
                <span className="ml-1">this month</span>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift cursor-pointer" onClick={() => navigate('/pipeline')} data-testid="stat-deals">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Deals</p>
                  <p className="text-3xl font-bold font-heading mt-1">{stats?.total_deals || 0}</p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-xl">
                  <Briefcase className="h-6 w-6 text-amber-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <span className="text-green-500 font-medium">{stats?.closed_this_month || 0}</span>
                <span className="text-muted-foreground ml-1">closed this month</span>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift cursor-pointer" onClick={() => navigate('/email')} data-testid="stat-emails">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Emails Sent</p>
                  <p className="text-3xl font-bold font-heading mt-1">{stats?.emails_sent || 0}</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-xl">
                  <Mail className="h-6 w-6 text-blue-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-muted-foreground">
                <span>Total outreach emails</span>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift" data-testid="stat-tasks">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Today's Tasks</p>
                  <p className="text-3xl font-bold font-heading mt-1">{todayTasks.length}</p>
                </div>
                <div className="p-3 bg-purple-500/10 rounded-xl">
                  <CheckCircle className="h-6 w-6 text-purple-500" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-muted-foreground">
                <Clock className="h-4 w-4 mr-1" />
                <span>Due today</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pipeline Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="lg:col-span-2" data-testid="pipeline-overview">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Pipeline Overview</CardTitle>
                  <CardDescription>Deals by stage</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/pipeline')}>
                  View Pipeline <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats?.by_stage && Object.entries(stats.by_stage).map(([stage, count]) => (
                  <div key={stage} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                      <span className="text-sm font-medium">{stage}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((count / (stats.total_deals || 1)) * 100, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-muted-foreground w-8">{count}</span>
                    </div>
                  </div>
                ))}
                {(!stats?.by_stage || Object.keys(stats.by_stage).length === 0) && (
                  <p className="text-center text-muted-foreground py-4">No deals yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Loan Types Distribution */}
          <Card data-testid="loan-types-chart">
            <CardHeader>
              <CardTitle className="font-heading">By Loan Type</CardTitle>
              <CardDescription>Distribution of deals</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.by_loan_type && Object.entries(stats.by_loan_type).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <span className="text-sm font-medium">{type}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
                {(!stats?.by_loan_type || Object.keys(stats.by_loan_type).length === 0) && (
                  <p className="text-center text-muted-foreground py-4">No data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Today's Tasks & Recent Contacts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Tasks */}
          <Card data-testid="today-tasks">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Today's Tasks
                  </CardTitle>
                  <CardDescription>{todayTasks.length} tasks due today</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                {todayTasks.length > 0 ? (
                  <div className="space-y-3">
                    {todayTasks.map((task) => (
                      <div 
                        key={task.task_id} 
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                        data-testid={`task-${task.task_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            {getTaskIcon(task.type)}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{task.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {task.type.replace('_', ' ')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleCompleteTask(task.task_id)}
                          data-testid={`complete-task-${task.task_id}`}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No tasks due today!</p>
                    <p className="text-sm">You're all caught up</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Recent Contacts */}
          <Card data-testid="recent-contacts">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Recent Contacts</CardTitle>
                  <CardDescription>Latest leads added</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/contacts')}>
                  View All <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                {recentContacts.length > 0 ? (
                  <div className="space-y-3">
                    {recentContacts.map((contact) => (
                      <div 
                        key={contact.contact_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                        onClick={() => navigate(`/contacts/${contact.contact_id}`)}
                        data-testid={`contact-${contact.contact_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-primary/10 text-primary font-medium">
                              {contact.first_name?.[0]}{contact.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-sm">
                              {contact.first_name} {contact.last_name}
                            </p>
                            <p className="text-xs text-muted-foreground">{contact.email}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {contact.loan_types?.slice(0, 2).map((type) => (
                            <Badge key={type} variant="outline" className="text-xs">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No contacts yet</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => navigate('/contacts')}
                    >
                      Add your first contact
                    </Button>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
